import { useEffect } from "react";
import useWebSocket from "react-use-websocket";
import { Options } from "react-use-websocket/src/lib/types";
import { useImmer } from "use-immer";

import {
  type WebsocketAdminChannel,
  type WebsocketData,
} from "@intelligems/ig-types";
import { createChunkedWebSocketHook } from "@intelligems/ig-utils";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useLoggedInUser } from "@src/api/user-auths/login";

import { getAuthToken } from "../utils";

// Create the chunked WebSocket hook once
const useChunkedWebSocket = createChunkedWebSocketHook<
  WebsocketData,
  ReturnType<typeof useWebSocket>,
  Options
>(useWebSocket);

export const useWebsocket = (
  opts?: Options,
  defaultUrl = import.meta.env.VITE_WEBSOCKET_URL as string
) => {
  const org = useGetOrg();
  const user = useLoggedInUser();
  const [url, setUrl] = useImmer<string | null>(null);

  const token = getAuthToken();
  const orgId = org.data?.org?.id;
  const email = user.data?.user?.email;

  useEffect(() => {
    if (orgId && email) {
      setUrl(`${defaultUrl}`);
    }
  }, [orgId, email, setUrl, defaultUrl]);

  return useChunkedWebSocket(orgId && email && token ? url : null, {
    queryParams: orgId && email && token ? { orgId, email, token } : undefined,
    ...opts,
  });
};

export const useAdminWebsocket = (
  adminChannel: WebsocketAdminChannel,
  opts?: Options,
  defaultUrl = import.meta.env.VITE_WEBSOCKET_URL as string
) => {
  const [url, setUrl] = useImmer<string | null>(null);
  const token = getAuthToken();

  useEffect(() => {
    if (token) {
      setUrl(`${defaultUrl}`);
    }
  }, [token, setUrl, defaultUrl]);

  return useChunkedWebSocket(token ? url : null, {
    queryParams: token ? { adminChannel, token } : undefined,
    ...opts,
  });
};
